"use client"
import { AlertCircle, Calendar, CheckCircle, Clock, Edit, FileText, Loader2, Plus, RefreshCw, Trash2, User } from 'lucide-react';
import { useEffect, useState } from 'react';

interface StatData {
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
}

interface StatsResponse {
  totalLogs: StatData;
  successLogs: StatData;
  failureLogs: StatData;
  pendingLogs: StatData;
  createActions: StatData;
  editActions: StatData;
  deleteActions: StatData;
  loginActions: StatData;
  thisMonthLogs: StatData;
  lastMonthLogs: StatData;
}

const statConfig = [
  {
    key: 'totalLogs' as keyof StatsResponse,
    name: 'Total Logs',
    icon: FileText,
    color: 'bg-blue-500'
  },
  {
    key: 'successLogs' as keyof StatsResponse,
    name: 'Success',
    icon: CheckCircle,
    color: 'bg-green-500'
  },
  {
    key: 'failureLogs' as keyof StatsResponse,
    name: 'Failures',
    icon: AlertCircle,
    color: 'bg-red-500'
  },
  {
    key: 'pendingLogs' as keyof StatsResponse,
    name: 'Pending',
    icon: Clock,
    color: 'bg-yellow-500'
  },
  {
    key: 'createActions' as keyof StatsResponse,
    name: 'Create Actions',
    icon: Plus,
    color: 'bg-purple-500'
  },
  {
    key: 'editActions' as keyof StatsResponse,
    name: 'Edit Actions',
    icon: Edit,
    color: 'bg-indigo-500'
  },
  {
    key: 'deleteActions' as keyof StatsResponse,
    name: 'Delete Actions',
    icon: Trash2,
    color: 'bg-orange-500'
  },
  {
    key: 'loginActions' as keyof StatsResponse,
    name: 'Login Actions',
    icon: User,
    color: 'bg-teal-500'
  },
  {
    key: 'thisMonthLogs' as keyof StatsResponse,
    name: 'This Month',
    icon: Calendar,
    color: 'bg-cyan-500'
  }
];

interface AuditLogsStatsProps {
  refreshKey?: number;
}

export default function AuditLogsStats({ refreshKey }: AuditLogsStatsProps) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/audit-logs/stats');
      if (!res.ok) throw new Error('Failed to load statistics');
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
      } else {
        throw new Error(data.error || 'Failed to load statistics');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [refreshKey]);

  // Also listen for audit logs refresh events
  useEffect(() => {
    const handleRefresh = () => {
      // Small delay to ensure database is updated
      setTimeout(() => {
        fetchStats();
      }, 100);
    };

    window.addEventListener('audit-logs-refresh', handleRefresh);
    return () => {
      window.removeEventListener('audit-logs-refresh', handleRefresh);
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
          <span className="text-gray-600">Loading statistics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchStats}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2 mx-auto"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Audit Logs Statistics</h2>
        <p className="text-sm text-gray-500 mt-1">Overview of system activity and actions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {statConfig.map((cfg) => {
          const statData = stats[cfg.key];
          const Icon = cfg.icon;

          return (
            <div
              key={cfg.key as string}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-600 mb-1">{cfg.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{statData.value}</p>
                </div>
                <div className={`rounded-md border border-gray-200 p-2 shadow-sm ${cfg.color} transition-transform hover:scale-110`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

